import React from 'react';
import {
  Box,
  Grid,
  Divider,
  Avatar,
  Typography,
  CircularProgress,
  makeStyles,
  MenuItem
} from '@material-ui/core';
import {
  MoreVert as MoreIcon
} from '@material-ui/icons';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import moment from 'moment';

import SpecComment from '../../../models/SpecComment';
import SpecCommentReport from '../../../models/SpecCommentReport';
import { MenuIconButton } from '../../Buttons';
import { useDialog } from '../../../context/Dialog';
import utils from '../../../libs/utils';
import auth from '../../../libs/auth';
import useLoadingStatus from '../../../hooks/useLoadingStatus';

interface CommentItemProps {
  comment: SpecComment
  divider?: boolean
  showMenu?: boolean
  onDeleted?: () => void
}

interface CommentAction {
  name: string
  onClick: () => void
  show: boolean
}

const useStyle = makeStyles(() => ({
  authorName: {
    fontWeight: 'bold',
    opacity: 0.85,
    margin: 0
  },
  timestamp: {
    fontSize: '0.75em',
    lineHeight: '1',
    opacity: 0.75,
  }
}));

export const CommentItem = (props: CommentItemProps) => {
  const { divider, comment, onDeleted = () => {}, showMenu = true } = props;
  const { isLoading, executeWithStatus } = useLoadingStatus();
  const classes = useStyle();
  const { alert, confirm, prompt } = useDialog();

  const session = auth.getSession();
  const isOwner = session?.accountID === comment.authorId;
  const isAdmin = ['admin', 'teacher'].includes(session?.accountType || '');

  // delete comment
  const deleteComment = async () => {
    await executeWithStatus(async () => {
      const confirmed = await confirm('Are you sure to delete the comment?', {
        title: 'Delete Confirmation'
      });
      if (confirmed) {
        await comment.destroy();
        onDeleted();
      }
    }, e => alert(utils.getRequestErrorMessage(e),{
      title: 'Delete Comment Error'
    }));
  };

  // hide comment
  const hideComment = async () => {
    await executeWithStatus(async () => {
      const confirmed = await confirm('Are you sure to hide the comment?', {
        title: 'Hide Confirmation'
      });
      if (confirmed) {
        await comment.destroy();
        onDeleted();
      }
    }, e => alert(utils.getRequestErrorMessage(e),{
      title: 'Hide Comment Error'
    }));
  };

  // report comment
  const reportComment = async () => {
    await executeWithStatus(async () => {
      const description = await prompt((
        <Box width="100vh" maxWidth="100%">
          <ReactMarkdown
            plugins={[gfm]}
            source={comment.content}
          />
        </Box>
      ), {
        title: 'Report Comment',
        fieldProps: {
          placeholder: 'Enter report description',
          required: true,
          fullWidth: true,
          multiline: true,
          rows: 3
        }
      });
      if (typeof description === 'string') {
        await SpecCommentReport.create({
          commentId: comment.id,
          description: description
        });

        await alert('Report has been sent successfully. Admins will resolve this concern.', {
          title: 'Report sent'
        });
      }
    }, e => alert(utils.getRequestErrorMessage(e),{
      title: 'Report Note Error'
    }));
  };

  const actions: CommentAction[] = [
    {
      name: 'Delete',
      onClick: deleteComment,
      show: isOwner
    },
    {
      name: 'Remove',
      onClick: hideComment,
      show: !isOwner && isAdmin
    },
    {
      name: 'Report',
      onClick: reportComment,
      show: !isOwner
    }
  ].filter(a => a.show);

  return (
    <>
      <Grid container>
        <Grid item xs>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Avatar src={comment.author?.picture} />
            </Grid>
            <Grid item>
              <Typography className={classes.authorName}>{comment.author?.fullName || comment.authorId}</Typography>
              <Typography className={classes.timestamp}>{moment(comment.createdAt).format('MMM D, YYYY [at] h:mm:ss a')}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <Box pr={1}>
            {showMenu && (
              <MenuIconButton
                icon={isLoading ? <CircularProgress size="1em" color="inherit" /> : <MoreIcon />}
                iconButtonProps={{size: 'small', disabled: isLoading}}>
                {({ setAnchorEl }) => actions.map(action => (
                  <MenuItem
                    key={action.name}
                    onClick={() => {
                      setAnchorEl(null);
                      action.onClick();
                    }}>
                    { action.name }
                  </MenuItem>
                ))}
              </MenuIconButton>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box py={1} pl={6.2} pr={2.5} className="mde-preview">
        <div className="mde-preview-content" style={{padding: 0}}>
          <ReactMarkdown
            plugins={[gfm]}
            source={comment.content}
          />
        </div>
      </Box>

      {divider && (<Divider />)}
    </>
  );
};

export default CommentItem;
